import { EntityChip, SEVERITY, SeverityLevel, TableColumn, Tooltip } from '@silverfort/sf-components';
import { ServiceAccount } from '../ServiceAccountsTypes';
import ProtectionBadge from './ProtectionBadge';
import { bigNumberToText } from '../../../../components/utils';
import { getAccountType } from '@silverfort-nodelibs/common/utils/service-account-utils';
import { calculateBaselineDays } from '../utils/ServiceAccountsUtils';
import DisplayNameColumn from './DisplayNameColumn';
import TypeColumn from './TypeColumn';
import { DateFormat, formatDate } from '../../../../utils/dates';

const calcWidth = (width: number) => {
    return `min-w-[${width}vw] max-w-[${width}vw]`;
};

const buildSAColumns = (
    onDisplayNameClick: (guid: string) => void,
    shouldShowRiskCol: boolean,
    shouldShowProtectionCol: boolean
): TableColumn<ServiceAccount>[] => {
    const baseColumns: TableColumn<ServiceAccount>[] = [
        {
            columnKey: 'calculated_name',
            title: 'Name',
            columnClassName: '[&]:min-w-72 overflow-hidden text-ellipsis',
            render: (data) => (
                <DisplayNameColumn
                    displayName={data.calculated_name || data.display_name}
                    domainName={data.domain_name}
                    guid={data.guid}
                    onDisplayNameClick={onDisplayNameClick}
                />
            )
        },
        {
            columnKey: 'is_protected',
            title: 'Protection',
            columnClassName: calcWidth(14),
            render: (data) => (
                <ProtectionBadge
                    isProtected={data.is_protected}
                    protectedMode={data.protected_mode}
                    smartPolicyName={data.smart_policy_name}
                />
            )
        },
        {
            columnKey: 'last_use',
            title: 'Last seen',
            columnClassName: calcWidth(12),
            render: (data) =>
                data.last_use ? formatDate(new Date(data.last_use), DateFormat.SHORT_DATE_MD, true) : 'Not seen'
        },
        {
            columnKey: 'risk',
            title: 'Risk level',
            columnClassName: calcWidth(10),
            render: (data) => {
                return (
                    <div className="min-w-[3vw] max-w-[3vw]">
                        <SeverityLevel severity={data.risk as SEVERITY} />
                    </div>
                );
            }
        },
        {
            columnKey: 'devices',
            title: 'Sources',
            columnClassName: calcWidth(8)
        },
        {
            columnKey: 'resources',
            title: 'Destinations',
            columnClassName: calcWidth(10)
        },
        {
            columnKey: 'auths_count',
            title: 'Authentications',
            columnClassName: '[&]:min-w-32',
            render: (data) => bigNumberToText(data.auths_count)
        },
        {
            columnKey: 'baseline_change',
            title: 'Baseline change',
            columnClassName: '[&]:min-w-36',
            // todo: remove "not seen" once the baseline_change column "not null" migration is done
            render: (data) => (data.baseline_change ? calculateBaselineDays(data.baseline_change) : 'Not seen')
        },
        {
            columnKey: 'type_resolved',
            title: 'Type',
            columnClassName: '[&]:min-w-36',
            render: (data) => (
                <TypeColumn
                    type={getAccountType(data.is_gmsa, data.type_resolved, data.remove_reason)}
                    category={data.type_resolved}
                />
            )
        },
        {
            columnKey: 'account_owners',
            title: 'Owners',
            columnClassName: '!min-w-[10rem]',
            disableSort: true,
            render: (data) => {
                const owners = [
                    ...(data.account_owners?.map((owner) => owner.user_display_name || owner.group_display_name) || []),
                    ...(data.custom_owners?.map((owner) => owner.owner_value) || [])
                ];
                if (!owners || owners.length === 0) {
                    return <div></div>;
                }
                const hiddenOnwersCount = owners.length - 1;
                return (
                    <div className="flex gap-2">
                        <EntityChip
                            className="max-w-26 [&>div:first-child]:min-w-0"
                            text={
                                <Tooltip title={owners[0]}>
                                    <span className="block truncate">{owners[0]}</span>
                                </Tooltip>
                            }
                        />
                        {hiddenOnwersCount > 0 && <EntityChip className="flex-shrink-0" text={`+${hiddenOnwersCount}`} />}
                    </div>
                );
            }
        },
        {
            columnKey: 'last_rotation',
            title: 'Last rotation',
            columnClassName: '[&]:min-w-36',
            render: (data) =>
                data.last_rotation
                    ? formatDate(new Date(data.last_rotation * 1000), DateFormat.SHORT_DATE_MD, true)
                    : ''
        }
    ];

    return baseColumns.filter(
        (col) =>
            (col.columnKey !== 'risk' || shouldShowRiskCol) &&
            (col.columnKey !== 'is_protected' || shouldShowProtectionCol)
    );
};
export default buildSAColumns;
